import { inject, Service } from '@angular/core';
import {environment} from '../../environments/environment';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import {GoogleService} from './google-service';
import {map, Observable} from 'rxjs';
import {EntitySureFlowLoop} from '../model/entity-sure-flow-loop';
import {WorkingSureFlowLoop} from '../model/working-sure-flow-loop';
import {SureFlowScheduleCreationInput} from '../model/sure-flow-schedule-creation-input';
import {Entity} from '../entity/entity';
import {SureFlowEntityRatingInput} from '../model/sure-flow-entity-rating-input';

@Service()
export class SureFlowLoopService {
  private readonly http = inject(HttpClient);
  private readonly googleService = inject(GoogleService);


  private urlBase = environment.serviceUrl + 'sureFlow/loop/';

  getEntityLoops(entityId: string, entityRevision: number): Observable<EntitySureFlowLoop[]> {
    const url = this.urlBase + 'entity/list/' + entityId + '/' + entityRevision;
    return this.http.get<EntitySureFlowLoop[]>(url, {headers: this.getRequestHeaders()});
  }

  exportEntityLoops(entityId: string, entityRevision: number): Observable<Blob> {
    const url = this.urlBase + 'entity/export/' + entityId + '/' + entityRevision;
    return this.http.get(url, {
      headers: this.getRequestHeaders(),
      observe: 'response',
      responseType: 'blob'
    }).pipe(map((response: any) => {
      return new Blob([response.body], {type: response.headers.get('Content-Type')});
    }));
  }

  private getRequestHeaders(): HttpHeaders {
    return new HttpHeaders().set(GoogleService.TokenHeader, this.googleService.getAccessToken());
  }

  getEntityLoop(loopId: number): Observable<EntitySureFlowLoop> {
    const url = this.urlBase + 'entity/' + loopId;
    return this.http.get<EntitySureFlowLoop>(url, {headers: this.getRequestHeaders()});
  }

  saveEntityLoop(input: EntitySureFlowLoop): Observable<EntitySureFlowLoop> {
    const url = this.urlBase + 'entity';
    return this.http.post<EntitySureFlowLoop>(url, JSON.stringify(input), {headers: this.getPostHeaders()});
  }

  private getPostHeaders(): HttpHeaders {
    return this.getRequestHeaders().set('Content-Type', 'application/json');
  }

  deleteEntityLoop(loopId: number, entityId: string, entityRevision: number): Observable<string> {
    const url = this.urlBase + 'entity/' + entityId + '/' + entityRevision + '/' + loopId;
    return this.http.delete(url, {headers: this.getRequestHeaders(), responseType: 'text'});
  }

  getWorkingLoops(personId: string): Observable<WorkingSureFlowLoop[]> {
    const url = this.urlBase + 'working/list/' + encodeURIComponent(personId);
    return this.http.get<WorkingSureFlowLoop[]>(url, {headers: this.getRequestHeaders()});
  }

  exportWorkingLoops(personId: string): Observable<Blob> {
    const url = this.urlBase + 'working/export/' + personId;
    return this.http.get(url, {
      headers: this.getRequestHeaders(),
      observe: 'response',
      responseType: 'blob'
    }).pipe(map((response: any) => {
      return new Blob([response.body], {type: response.headers.get('Content-Type')});
    }));
  }

  getWorkingLoop(loopId: number): Observable<WorkingSureFlowLoop> {
    const url = this.urlBase + 'working/' + loopId;
    return this.http.get<WorkingSureFlowLoop>(url, {headers: this.getRequestHeaders()});
  }

  saveWorkingLoop(input: WorkingSureFlowLoop): Observable<WorkingSureFlowLoop> {
    const url = this.urlBase + 'working';
    return this.http.post<WorkingSureFlowLoop>(url, JSON.stringify(input), {headers: this.getPostHeaders()});
  }

  deleteWorkingLoop(loopId: number): Observable<string> {
    const url = this.urlBase + 'working/' + loopId;
    return this.http.delete(url, {headers: this.getRequestHeaders(), responseType: 'text'});
  }

  createSchedule(input: SureFlowScheduleCreationInput): Observable<Entity> {
    const url = this.urlBase + 'createSchedule';
    return this.http.post<Entity>(url, JSON.stringify(input), {headers: this.getPostHeaders()});
  }

  copyEntityLoop(input: SureFlowEntityRatingInput): Observable<EntitySureFlowLoop> {
    const url = this.urlBase + 'entity/copy';
    return this.http.post<EntitySureFlowLoop>(url, JSON.stringify(input), {headers: this.getPostHeaders()});
  }

}
